const captchaPublicKey = process.env.REACT_APP_CAPTCHA_PUBLIC_KEY
const basename = "/app"

// Both are injected at build time by .forgejo/workflows/pagews-app.yml
// from SSM (/${env}/pagews/{authDomain,projectDomainId}). Read through
// here rather than off process.env at the point of use, so every consumer sees
// the same value and the build-time contract lives in one file.
const authDomain = process.env.REACT_APP_AUTH_DOMAIN
const projectDomainId = process.env.REACT_APP_PROJECT_DOMAIN_ID

// The distribution's own *.cloudfront.net name — what a tenant's edge must send
// a composed page's request to, and the one value the Domains screen's setup
// instructions cannot derive from anything else it knows.
//
// It cannot be the app's hostname: the renderer refuses to answer when the
// viewer's host is this app's domain, which is what keeps tenant-authored script
// off the origin holding the editor's session (P142). So it is the cdn stack's
// DistributionDomainName, injected at build time.
//
// UNDEFINED IS A VALID STATE and the screen handles it: a local checkout has no
// distribution at all, and an environment whose cdn stack has not been deployed
// yet has no such output. The three builders that write it — pagews-app.yml,
// branch-env.sh and local/envs — all resolve it non-fatally for that reason. An
// app must never fail to build because a hostname could not be looked up.
const sitesOrigin = process.env.REACT_APP_SITES_ORIGIN

// CloudWatch RUM. Injected at build time by .forgejo/workflows/*-app.yml from
// the outputs of the ${env}-repoflow-system-observability-stack. All three are
// undefined in local dev, which leaves RUM inert — see
// @repoflow/components/observability.
const rum = {
    applicationId: process.env.REACT_APP_RUM_APP_MONITOR_ID,
    identityPoolId: process.env.REACT_APP_RUM_IDENTITY_POOL_ID,
    region: process.env.REACT_APP_RUM_REGION,
    // Baked from the app monitor's SessionSampleRate. The client samples on
    // its own side, so without this every session is recorded regardless of
    // what the monitor says. Absent or unparseable falls back to the client
    // default (1) — only reachable when RUM is inert anyway.
    sessionSampleRate: Number.isFinite(parseFloat(process.env.REACT_APP_RUM_SESSION_SAMPLE_RATE))
        ? parseFloat(process.env.REACT_APP_RUM_SESSION_SAMPLE_RATE)
        : undefined,
}

// THE EDITOR'S PREVIEW FRAME, and the one URL this app builds deliberately
// cross-origin.
//
// A tenant's section carries a `<script>`, and the editor's pane has to run it
// to show what the page does. Until P142 that pane was a route of this
// application, so the script ran with this application's origin — a sandboxed
// `srcDoc` everywhere else, and here the real thing. Serving the SAME route
// from the sites hostname moves it to an origin that holds nothing: no session,
// no storage, and no access to the document it is embedded in.
//
// `?editor=` is how the frame learns which window may drive it. It cannot be
// inferred over there — the frame's own origin is now the one origin the editor
// is not on — and it cannot be left to `"*"`, because the payload is the page
// being edited. See `editorOrigin` in preview/mount.
//
// UNDEFINED `sitesOrigin` FALLS BACK TO THIS ORIGIN, which is the state a local
// checkout and an undeployed cdn stack are in. That keeps the editor working
// where there is no second hostname to move to, and it means the exposure this
// closes stays open in exactly those places. It is a deployment property, not a
// runtime choice: every builder that writes the variable resolves it
// non-fatally, so a missing distribution name must never be a broken editor.
//
// The trailing slash is on purpose: the edge answers `/app/preview` with a 301
// to `/app/preview/`, and a redirect on the frame's first load is a document
// the parent has to wait an extra round trip for.
const previewSrc = () =>
    sitesOrigin
        ? `https://${sitesOrigin}${basename}/preview/?editor=${encodeURIComponent(window.location.origin)}`
        : `${basename}/preview`

export {
    captchaPublicKey,
    basename,
    authDomain,
    projectDomainId,
    sitesOrigin,
    previewSrc,
    rum
}
